/**
 * The raw material behind the findings: what was read, and what was asked.
 *
 * The link panel is where the plan's hardest rule becomes visible. Broken and
 * unverifiable are two lists with two different headings, and the unverifiable
 * one says out loud that those links may be perfectly fine. An operator who
 * cannot see that distinction will quote a bot wall as a dead link in a first
 * email, and the prospect will disprove it by clicking.
 */
import { ExternalLink, FileText, Link2, ShieldQuestion } from "lucide-react";
import type { BrainCrawl, BrainLinkIssue, BrainLinks } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";

export function BrainLinksPanel({ links, t }: { links: BrainLinks; t: T }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t("brain.links.summary", { checked: links.checked, total: links.total })}
      </p>

      <IssueList
        icon={Link2}
        tone="var(--color-destructive)"
        title={t("brain.links.broken", { count: links.broken.length })}
        note={t("brain.links.brokenNote")}
        issues={links.broken}
        empty={t("brain.links.noneBroken")}
        t={t}
      />

      {links.unverifiable.length > 0 ? (
        <IssueList
          icon={ShieldQuestion}
          tone="var(--color-muted-foreground)"
          title={t("brain.links.unverifiable", { count: links.unverifiable.length })}
          note={t("brain.links.unverifiableNote")}
          issues={links.unverifiable}
          empty=""
          t={t}
        />
      ) : null}
    </div>
  );
}

function IssueList({
  icon: Icon,
  tone,
  title,
  note,
  issues,
  empty,
  t,
}: {
  icon: typeof Link2;
  tone: string;
  title: string;
  note: string;
  issues: BrainLinkIssue[];
  empty: string;
  t: T;
}) {
  return (
    <section>
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="size-4" style={{ color: tone }} />
        {title}
      </h4>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>

      {issues.length === 0 ? (
        empty ? <p className="mt-2 text-sm text-muted-foreground">{empty}</p> : null
      ) : (
        <ul className="mt-2 space-y-1">
          {issues.map((issue) => (
            <li key={issue.url} className="rounded-xl bg-[var(--crm-sunk)] px-3 py-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0 text-xs font-semibold tabular-nums" style={{ color: tone }}>
                  {issue.code}
                </span>
                <a
                  href={issue.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="min-w-0 flex-1 truncate underline-offset-2 hover:underline"
                >
                  {issue.text || issue.url}
                </a>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t(`brain.links.scope.${issue.scope}`)}
                </span>
              </div>
              {issue.foundOn[0] ? (
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {t("brain.links.foundOn", { url: issue.foundOn[0], count: issue.foundOn.length })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** What the crawl actually read. Proof that a thin result was thin on purpose. */
export function BrainPagesPanel({ crawl, t }: { crawl: BrainCrawl; t: T }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t("brain.pages.summary", { count: crawl.pages.length })}{" "}
        {t(`brain.pages.discovery.${crawl.discovery}`)}
      </p>

      <ul className="space-y-1">
        {crawl.pages.map((page) => (
          <li key={page.url} className="rounded-xl bg-[var(--crm-sunk)] px-3 py-2">
            <div className="flex items-start gap-2">
              <FileText className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{page.title || t("brain.pages.noTitle")}</span>
                <a
                  href={page.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex max-w-full items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  <ExternalLink className="size-3 shrink-0" />
                  <span className="truncate">{page.url}</span>
                </a>
              </span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {t("brain.pages.words", { count: page.words })}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
